import React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Layers, Code2 } from 'lucide-react';

// Import components
import PricingSection from '../components/PricingSection';

const Index = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  setLoading(true);
  setError(null);
  setSuccess(null);

  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    const res = await fetch(
      `${backendUrl}/contact`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }
    );

    // Try to parse JSON, fallback to plain text
    let data: any;
    try {
      data = await res.json();
    } catch {
      data = { message: await res.text() };
    }

    if (res.ok) {
      setSuccess(data.message || "Message sent successfully!");
      setForm({ name: "", email: "", message: "" });
    } else {
      setError(data.error || data.message || "Failed to send message.");
    }
  } catch (err) {
    setError("Something went wrong. Please try again.");
  } finally {
    setLoading(false);
  }
};

  const handlePlanSelect = (
    plan: 'Starter' | 'Professional' | 'Enterprise',
    message: string
  ) => {
    setForm((prev) => ({ ...prev, message }));
    const contactEl = document.getElementById('contact');
    if (contactEl && 'scrollIntoView' in contactEl) {
      contactEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Give the scroll a moment, then focus the textarea
    setTimeout(() => {
      const textarea = document.getElementById('message') as HTMLTextAreaElement | null;
      textarea?.focus();
    }, 400);
  };
  useEffect(() => {
    setIsVisible(true);
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 relative">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      {/* Faint top glow */}
      <div className="absolute inset-x-0 top-0 h-[420px] pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(167,139,250,0.07), transparent)' }} />

      {/* Header — floating bar */}
      <header className="relative z-20 px-4 sm:px-6 lg:px-8 pt-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center rounded-xl border border-zinc-800 bg-[#0d0d0d]/90 backdrop-blur px-5 py-3">
            <Link to="/" className="flex items-center">
              <img src="/pattrnworks_logo_transparent_small.png" alt="PattrnWorks" className="h-9 brightness-0 invert" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Services</a>
              <Link to="/projects" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Work</Link>
              <Link to="/careers" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Careers</Link>
              <a href="#contact" className="bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-4 py-2 rounded-md transition-colors duration-200 flex items-center">
                Start a project
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </a>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-zinc-400 hover:text-zinc-100 focus:outline-none"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12M6 6l12 12M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-zinc-800 bg-[#0a0a0a]/95 backdrop-blur-lg fixed left-0 right-0 z-[9999] shadow-lg top-[86px] w-full">
              <nav className="flex flex-col space-y-4 pb-4 px-4">
                <a href="#services" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 px-3 py-2 hover:text-zinc-100 transition-colors duration-200">Services</a>
                <Link to="/projects" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 px-3 py-2 hover:text-zinc-100 transition-colors duration-200">Work</Link>
                <Link to="/careers" onClick={() => setMobileMenuOpen(false)} className="text-zinc-300 px-3 py-2 hover:text-zinc-100 transition-colors duration-200">Careers</Link>
                <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="bg-zinc-100 text-zinc-900 font-medium px-5 py-2 rounded-md transition-colors duration-200 w-full mt-2">
                  Start a project
                </a>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-24 overflow-hidden">
        {/* Faint woven-mark watermark */}
        <img
          src="/favicon.png"
          alt=""
          aria-hidden="true"
          className="absolute -right-40 -top-24 w-[560px] opacity-[0.06] brightness-0 invert pointer-events-none select-none"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="md:grid md:grid-cols-2 md:gap-12 items-center">
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex items-center gap-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-violet-400"></span>
                <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">AI-native development agency</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-zinc-100">
                Your product, engineered for the AI era.
              </h1>
              <p className="mt-6 text-lg text-zinc-400 max-w-xl">
                We design and ship intelligent software — AI products, agents, APIs, and web platforms — with the rigor of an in-house team.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact"
                  className="bg-zinc-100 hover:bg-violet-400 text-zinc-900 font-medium px-8 py-3 rounded-md transition-all duration-200 flex items-center justify-center"
                >
                  Start a project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
                <Link
                  to="/projects"
                  className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-zinc-100 px-8 py-3 rounded-md transition-all duration-200 flex items-center justify-center"
                >
                  See the work
                </Link>
              </div>
            </div>

            {/* Code editor card — IDE mock */}
            <div className={`mt-12 md:mt-0 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="rounded-xl border border-zinc-800 bg-[#0d0d0f] shadow-2xl shadow-black/60 overflow-hidden">
                {/* Title bar */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/80">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700"></span>
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700"></span>
                      <span className="w-2.5 h-2.5 rounded-full bg-zinc-700"></span>
                    </div>
                    <span className="flex items-center gap-1.5 font-mono text-xs text-zinc-300">
                      <span className="text-violet-400 font-semibold">TS</span> agent.ts
                    </span>
                  </div>
                  <span className="font-mono text-xs text-zinc-500">TypeScript ▾</span>
                </div>

                {/* Code with line numbers */}
                <div className="flex font-mono text-[12px] leading-6 overflow-x-auto">
                  <div className="py-4 pl-4 pr-3 text-right text-zinc-700 select-none flex-shrink-0">
                    {Array.from({ length: 9 }, (_, i) => (
                      <div key={i}>{i + 1}</div>
                    ))}
                  </div>
                  <pre className="py-4 pr-5 flex-1"><code>
<span className="text-violet-300">import</span> <span className="text-zinc-300">{'{ createAgent }'}</span> <span className="text-violet-300">from</span> <span className="text-amber-200">'ai-sdk'</span><span className="text-zinc-500">;</span>{'\n'}
{'\n'}
<span className="text-violet-300">export const</span> <span className="text-zinc-200">agent</span> <span className="text-zinc-500">=</span> <span className="text-zinc-200">createAgent</span><span className="text-zinc-500">({'({'}</span>{'\n'}
{'  '}<span className="text-zinc-400">model</span><span className="text-zinc-500">:</span> <span className="text-amber-200">'gpt-5'</span><span className="text-zinc-500">,</span>{'\n'}
{'  '}<span className="text-zinc-400">instructions</span><span className="text-zinc-500">:</span> <span className="text-amber-200">'Ship great products'</span><span className="text-zinc-500">,</span>{'\n'}
{'  '}<span className="text-zinc-400">tools</span><span className="text-zinc-500">:</span> <span className="text-zinc-500">[</span><span className="text-zinc-300">research</span><span className="text-zinc-500">,</span> <span className="text-zinc-300">deploy</span><span className="text-zinc-500">],</span>{'\n'}
<span className="text-zinc-500">{'});'}</span>{'\n'}
{'\n'}
<span className="text-zinc-600">{'// shipped ✓'}</span>
                  </code></pre>
                </div>

                {/* Terminal panel */}
                <div className="border-t border-zinc-800/80">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/60">
                    <span className="font-mono text-xs text-zinc-400 flex items-center gap-2">
                      Terminal
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400"></span>
                    </span>
                    <span className="font-mono text-xs text-zinc-600">zsh</span>
                  </div>
                  <div className="px-4 py-3 font-mono text-xs flex items-center gap-2">
                    <span className="text-violet-400">✓</span>
                    <span className="text-zinc-300">Deployed to production</span>
                    <span className="text-zinc-600">· 2m ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Services Section */}
      <section id="services" className={`relative z-10 py-16 md:py-24 px-4 md:px-8 overflow-hidden transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <img
          src="/favicon.png"
          alt=""
          aria-hidden="true"
          className="absolute -left-48 bottom-0 w-[480px] opacity-[0.045] brightness-0 invert pointer-events-none select-none"
        />
        <div className="max-w-6xl mx-auto relative">
          <div className="mb-12">
            <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// What we build'}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold text-zinc-100">Services</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div className="group rounded-xl border border-zinc-800 bg-zinc-900/40 p-7 hover:border-violet-400/40 transition-all duration-300 flex flex-col">
              <div className="flex items-start justify-between mb-7">
                <Brain className="w-6 h-6 text-violet-400" />
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 group-hover:translate-x-1 transition-all duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-100 mb-3">AI Product Development</h3>
              <p className="text-zinc-400 text-sm leading-relaxed flex-1">From idea to intelligent product. We build AI-powered applications that solve real problems and scale.</p>
              <span className="mt-6 text-sm text-zinc-300 flex items-center gap-1.5 group-hover:text-violet-400 transition-colors duration-300">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </div>

            <div className="group rounded-xl border border-zinc-800 bg-zinc-900/40 p-7 hover:border-violet-400/40 transition-all duration-300 flex flex-col">
              <div className="flex items-start justify-between mb-7">
                <Layers className="w-6 h-6 text-violet-400" />
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 group-hover:translate-x-1 transition-all duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-100 mb-3">Custom AI Systems</h3>
              <p className="text-zinc-400 text-sm leading-relaxed flex-1">Domain-specific agents, copilots, and workflows tailored to your business. Built for performance, security, and scale.</p>
              <span className="mt-6 text-sm text-zinc-300 flex items-center gap-1.5 group-hover:text-violet-400 transition-colors duration-300">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </div>

            <div className="group rounded-xl border border-zinc-800 bg-zinc-900/40 p-7 hover:border-violet-400/40 transition-all duration-300 flex flex-col">
              <div className="flex items-start justify-between mb-7">
                <Code2 className="w-6 h-6 text-violet-400" />
                <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 group-hover:translate-x-1 transition-all duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-100 mb-3">Web Platforms & APIs</h3>
              <p className="text-zinc-400 text-sm leading-relaxed flex-1">Modern, fast, and resilient web applications and APIs. From marketing sites to complex platforms — built with today's best stack.</p>
              <span className="mt-6 text-sm text-zinc-300 flex items-center gap-1.5 group-hover:text-violet-400 transition-colors duration-300">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Process strip */}
      <section className="relative z-10 border-t border-zinc-800/80 py-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// How we work'}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { n: '01', t: 'Discover', d: 'Scope, constraints, and the sharpest version of the idea.' },
              { n: '02', t: 'Prototype', d: 'A working slice in days — not a deck in weeks.' },
              { n: '03', t: 'Build', d: 'Senior engineers ship in tight, reviewable increments.' },
              { n: '04', t: 'Ship & scale', d: 'Production-grade delivery, monitored and maintained.' },
            ].map((s) => (
              <div key={s.n} className="border-t border-zinc-800 pt-5">
                <span className="font-mono text-xs text-violet-400">{s.n}</span>
                <h4 className="mt-2 text-sm font-semibold text-zinc-100">{s.t}</h4>
                <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-t border-zinc-800/80">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// Engagement'}</span>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold text-zinc-100">Pricing</h2>
            <p className="mt-4 max-w-2xl text-lg text-zinc-400">
              Flexible plans to suit your roadmap. Scale as you grow.
            </p>
          </div>
          <div className="px-4 sm:px-6 lg:px-8">
            <PricingSection onPlanSelect={handlePlanSelect} />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative z-10 py-16 border-t border-zinc-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// Contact'}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-zinc-100">
              Let's build something.
            </h2>
            <p className="mt-4 text-lg text-zinc-400 max-w-3xl">
              Tell us what you're trying to ship — we'll come back with a point of view, a plan, and a team that can execute it.
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12">
                <h3 className="text-xl font-semibold text-zinc-100 mb-6">Get in touch</h3>
                <p className="text-zinc-400 text-sm mb-10">
                  Ready to build your AI-powered product, agent, or platform? Let's talk scope.
                </p>

                <div className="space-y-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg border border-zinc-700 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-violet-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="font-mono text-xs text-zinc-500">EMAIL</p>
                      <p className="text-zinc-200 text-sm">contact@pattrnworks.com</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg border border-zinc-700 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-violet-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="font-mono text-xs text-zinc-500">LOCATION</p>
                      <p className="text-zinc-200 text-sm">Toronto, ON</p>
                    </div>
                  </div>

                </div>
              </div>

              <div className="p-8 md:p-12 border-t md:border-t-0 md:border-l border-zinc-800">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block font-mono text-xs text-zinc-500 mb-2">NAME</label>
                    <input
                      type="text"
                      id="name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#0d0d0d] border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-violet-400/60 hover:border-zinc-600 transition-colors"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-mono text-xs text-zinc-500 mb-2">EMAIL</label>
                    <input
                      type="email"
                      id="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#0d0d0d] border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-violet-400/60 hover:border-zinc-600 transition-colors"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-mono text-xs text-zinc-500 mb-2">MESSAGE</label>
                    <textarea
                      id="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#0d0d0d] border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-violet-400/60 hover:border-zinc-600 transition-colors"
                      placeholder="Tell us about your project or requirements"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-zinc-100 hover:bg-violet-400 text-zinc-900 font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                  >
                    {loading ? "Sending..." : "Send message"}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </button>

                  {success && <p className="text-violet-400 text-sm mt-2">{success}</p>}
                  {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-zinc-800/80 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-8 lg:gap-x-16 items-start">
            <div className="max-w-sm">
              <img src="/pattrnworks_logo_transparent_small.png" alt="pattrnworks" className="h-10 mb-4 brightness-0 invert" />
              <p className="text-zinc-400 text-sm mb-6">
                An AI-native development agency. We design, build, and ship intelligent software — products, agents, platforms, and APIs.
              </p>
            </div>

            <div>
              <h3 className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase mb-4">Services</h3>
              <ul className="space-y-2">
                <li><span className="text-zinc-400 hover:text-violet-400 transition-colors text-sm cursor-pointer">AI Product Development</span></li>
                <li><span className="text-zinc-400 hover:text-violet-400 transition-colors text-sm cursor-pointer">Custom AI Systems</span></li>
                <li><span className="text-zinc-400 hover:text-violet-400 transition-colors text-sm cursor-pointer">Web Platforms & APIs</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-800/80 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-zinc-500 text-sm">© {new Date().getFullYear()} pattrnworks. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
